/* eslint-disable max-lines */
import type { GetDocListArgs } from 'frappe-js-sdk/lib/db/types'

import { defineStore } from 'pinia'

import type { DocAssigment } from '@/composables/iframe/useIframeDocAssignments'
import type { UserAuthResponse } from '@/stores/auth'
import type { FilterConditionOperator } from '@/types/external/2work/api/common/ConditionOperator'
import type { DocIdentifier } from '@/types/external/2work/api/common/DocIdentifier'
import type { FilterConditionSingleValue } from '@/types/external/2work/api/common/FilterCondition'
import type { RelationIdentifier } from '@/types/external/2work/api/common/RelationIdentifier'
import type { ActivityDocumentationCreationRequest } from '@/types/external/2work/api/request/AcitivityDocumentationCreationRequest'
import type { ActivityDocumentationUpdateRequest } from '@/types/external/2work/api/request/ActivityDocumentationUpdateRequest'
import type { ActivityRequest } from '@/types/external/2work/api/request/ActivityRequest.ts'
import type { BankAccount } from '@/types/external/2work/api/request/BankAccount.ts'
import type { CreateSignatureRequest } from '@/types/external/2work/api/request/CreateSignatureRequest'
import type { DocDeletionBulkRequest } from '@/types/external/2work/api/request/DocDeletionBulkRequest'
import type { DoctypeRelationsRequest } from '@/types/external/2work/api/request/DoctypeRelationsRequest'
import type { DoctypeTargetActivityFeedRequest } from '@/types/external/2work/api/request/DoctypeTargetActivityFeedRequest'
import type {
  DoctypeTargetRelationsRequest,
  DoctypeTargetRelationsSystemRequest,
} from '@/types/external/2work/api/request/DoctypeTargetRelationsRequest'
import type { EmailUpdateRequest } from '@/types/external/2work/api/request/EmailUpdateRequest'
import type { EventUpdateRequest } from '@/types/external/2work/api/request/EventUpdateRequest.ts'
import type { GetCompletionRateDataRequest } from '@/types/external/2work/api/request/GetCompletionRateDataRequest'
import type { GetDocsRequest } from '@/types/external/2work/api/request/GetDocsRequest'
import type { GetKanbanStatesRequest } from '@/types/external/2work/api/request/GetKanbanStatesRequest'
import type { GetKPIDataRequest } from '@/types/external/2work/api/request/GetKPIDataRequest'
import type { GetRatingsRequest } from '@/types/external/2work/api/request/GetRatingsRequest'
import type { GetSubscribedNewslettersRequest } from '@/types/external/2work/api/request/GetSubscribedNewsletterRequest'
import type { SignatureSubmitRequest } from '@/types/external/2work/api/request/SignatureSubmitRequest'
import type { TagAdditionBulkRequest } from '@/types/external/2work/api/request/TagAdditionBulkRequest'
import type { ToggleFavoriteStateRequest } from '@/types/external/2work/api/request/ToggleFavoriteStateRequest'
import type { UnsubscribeFromNewslettersRequest } from '@/types/external/2work/api/request/UnsubscribeFromNewslettersRequest'
import type {
  UpdateBRMOrganizationDocument,
  UpdateBRMOrganizationRequest,
} from '@/types/external/2work/api/request/UpdateBRMOrganizationRequest'
import type { UpdatePasswordRequest } from '@/types/external/2work/api/request/UpdatePasswordRequest'
import type {
  UpdateBRMPerson,
  UpdatePersonRequest,
} from '@/types/external/2work/api/request/UpdatePersonRequest'
import type { ActivityDocumentationCreationResponse } from '@/types/external/2work/api/response/AcitivityDocumentationCreationResponse'
import type {
  ActivityDocumentationResponse,
  EmailResponse,
  EventResponse,
} from '@/types/external/2work/api/response/ActivityResponse.ts'
import type { CalendarEvent } from '@/types/external/2work/api/response/CalendarEventsResponse.ts'
import type { DistributionListMembership } from '@/types/external/2work/api/response/DistributionListMembership'
import type { DocDeletion } from '@/types/external/2work/api/response/DocDeletion'
import type { DoctypeRelationsResponse } from '@/types/external/2work/api/response/DoctypeRelationsResponse'
import type { DoctypeTargetActivityFeedResponse } from '@/types/external/2work/api/response/DoctypeTargetActivityFeedResponse'
import type { DoctypeTargetRelationsResponse } from '@/types/external/2work/api/response/DoctypeTargetRelationsResponse'
import type { EventCreationResponse } from '@/types/external/2work/api/response/EventCreationResponse.ts'
import type { FavoriteToggle } from '@/types/external/2work/api/response/FavoriteToggle'
import type { GetCompletionRateResponse } from '@/types/external/2work/api/response/GetCompletionRateResponse'
import type {
  Permission,
  Permissions,
} from '@/types/external/2work/api/response/getDoctypePermissions'
import type { GetEmailInboxesResponse } from '@/types/external/2work/api/response/GetEmailInboxesResponse'
import type { GetKanbanStatesResponse } from '@/types/external/2work/api/response/GetKanbanStatesResponse'
import type { NotificationDashboards } from '@/types/external/2work/api/response/GetNotificationDashboard.ts'
import type {
  getShareUsersResponse,
  shareUser,
} from '@/types/external/2work/api/response/getShareUsersResponse'
import type { GetSubscribedNewslettersResponse } from '@/types/external/2work/api/response/GetSubscribedNewslettersResponse'
import type { GetUnreadCommunicationsResponse } from '@/types/external/2work/api/response/GetUnreadCommunicationsResponse'
import type { GetUnreadTodos } from '@/types/external/2work/api/response/GetUnreadTodos'
import type {
  GlobalSearchResponse,
  GlobalSearchResult,
} from '@/types/external/2work/api/response/GlobalSearchResponse'
import type { KPIData } from '@/types/external/2work/api/response/KPIData'
import type { PostMarkAsRead } from '@/types/external/2work/api/response/PostMarkAsRead.ts'
import type { RatingReponse } from '@/types/external/2work/api/response/RatingResponse'
import type { ResetUserPasswordResponse } from '@/types/external/2work/api/response/ResetUserPasswordResponse'
import type { ResponseWrapper } from '@/types/external/2work/api/response/ResponseWrapper'
import type {
  ReportPermission,
  ShareValue,
} from '@/types/external/2work/api/response/setPermissionForAllUsersForOneDocument.ts'
import type {
  SignatureRequestListResponse,
  SignatureRequestResponse,
  SignatureSubmitResponse,
} from '@/types/external/2work/api/response/SignatureRequestResponse'
import type {
  CreateSignatureResponse,
  SuggestedSignersResponse,
} from '@/types/external/2work/api/response/SuggestedSignersResponse'
import type { TagAddition } from '@/types/external/2work/api/response/TagAddition'
import type UserCalendarEvent from '@/types/external/2work/api/response/UserCalendarEvent.ts'
import type {
  BRMOrganization,
  BRMOrganizationBase,
  BRMPerson,
  Person,
} from '@/types/external/2work/doctypes/CustomDoctypes'
import type { BankDynamic } from '@/types/external/2work/doctypes/generatorOutput/Accounts/BankDynamic.ts'
import type { PaymentTermsTemplate } from '@/types/external/2work/doctypes/generatorOutput/Accounts/PaymentTermsTemplate'
import type { Address } from '@/types/external/2work/doctypes/generatorOutput/Contacts/Address'
import type { AddressCreation } from '@/types/external/2work/doctypes/generatorOutput/Contacts/AddressCreation'
import type { AddressDynamic } from '@/types/external/2work/doctypes/generatorOutput/Contacts/AddressDynamic'
import type { Contact } from '@/types/external/2work/doctypes/generatorOutput/Contacts/Contact'
import type { Communication } from '@/types/external/2work/doctypes/generatorOutput/Core/Communication'
import type { CommunicationCreation } from '@/types/external/2work/doctypes/generatorOutput/Core/CommunicationCreation'
import type { DynamicLink } from '@/types/external/2work/doctypes/generatorOutput/Core/DynamicLink'
import type { UserDynamic } from '@/types/external/2work/doctypes/generatorOutput/Core/UserDynamic'
import type { TWEvent } from '@/types/external/2work/doctypes/generatorOutput/Desk/TWEvent.ts'
import type { TWEventCreation } from '@/types/external/2work/doctypes/generatorOutput/Desk/TWEventCreation.ts'
import type { TWActivityDocumentation } from '@/types/external/2work/doctypes/generatorOutput/ERP2Work/TWActivityDocumentation'
import type { TWActivityDocumentationDynamic } from '@/types/external/2work/doctypes/generatorOutput/ERP2Work/TWActivityDocumentationDynamic'
import type { TWInvestorType } from '@/types/external/2work/doctypes/generatorOutput/ERP2Work/TWInvestorType'
import type { TWOrganizationAIResponse } from '@/types/external/2work/doctypes/generatorOutput/ERP2Work/TWOrganizationAIResponse'
import type { TWOtherType } from '@/types/external/2work/doctypes/generatorOutput/ERP2Work/TWOtherType'
import type { TWPhoneType } from '@/types/external/2work/doctypes/generatorOutput/ERP2Work/TWPhoneType'
import type { TWProjectBillingSummary } from '@/types/external/2work/doctypes/generatorOutput/ERP2Work/TWProjectBillingSummary'
import type { TWRole } from '@/types/external/2work/doctypes/generatorOutput/ERP2Work/TWRole'
import type { TWWeblinkType } from '@/types/external/2work/doctypes/generatorOutput/ERP2Work/TWWeblinkType'
import type { Country } from '@/types/external/2work/doctypes/generatorOutput/Geo/Country'
import type { PrintFormat } from '@/types/external/2work/doctypes/generatorOutput/Printing/PrintFormat.ts'
import type { PrintFormatCreation } from '@/types/external/2work/doctypes/generatorOutput/Printing/PrintFormatCreation.ts'
import type { IndustryType } from '@/types/external/2work/doctypes/generatorOutput/Selling/IndustryType'
import type { Company } from '@/types/external/2work/doctypes/generatorOutput/Setup/Company.ts'
import type { EmployeeDynamic } from '@/types/external/2work/doctypes/generatorOutput/Setup/EmployeeDynamic'
import type { KanbanBoardRefItem } from '@/types/kanban.ts'
import type {
  CompanyManagementRequest,
  CompanyManagementResponse,
  ManagementApplyRequest,
  ManagementApplyResponse,
  ManagementPersonMatches,
  ManagementPersonMatchesRequest,
} from '@/types/managementSuggestionTypes'
import type { ReportRefItem } from '@/types/reports.ts'

import { ONBOARDING_TOUR_VERSION } from '@/constants/onboarding'
import { RelationGroupType, RelationType } from '@/constants/relations'
import { useFrappeCallAPIStore } from '@/stores/apis/frappeCallAPI'
import { useFrappeDataAPIStore } from '@/stores/apis/frappeDataAPI'
import { getDefaultKanbanBoardName } from '@/utilities/kanban'
import { getCurrentLanguage } from '@/utilities/translations'

import type { DocAttachment } from '../../composables/iframe/useIframeDocAttachments'

type EmployeeListParameterList = (keyof EmployeeDynamic)[]
type UserListParameterList = (keyof UserDynamic)[]

interface HasDocumentPermissionParams {
  docname: string
  doctype: string
  perm_type: Permission
}

interface HasDocumentPermissionResponse {
  exists: boolean
  has_permission: boolean
}

interface UserOnboardingStatusResponse {
  completed: boolean
  current_tour_version: string
  completed_at?: null | string
  tour_version?: null | string
}

// ── Time Tracking Timer types (used in public store API) ────────────

interface TimerEmployee {
  employee_name: string
  name: string
}

interface TimerStatusResponse {
  activity_type: null | string
  customer: null | string
  description: string
  from_time: string
  is_billable: boolean
  name: string
  parent_project: null | string
  project: null | string
  title: string
}

interface TimerNameResult {
  from_time: string
  name: string
}

interface TimerActionResult {
  name: string
  status: string
}

interface TimerOptionsResponse {
  activity_types: { name: string }[]
  customers: { customer_name?: string; image?: null | string; name: string }[]
  projects: { customer?: null | string; name: string; project_name?: string }[]
}

type TranslatedTWPhoneType = TWPhoneType & { translated_tw_phone_type_name?: string }
type TranslatedTWRole = TWRole & { translated_tw_role_name?: string }
type TranslatedTWWeblinkType = TWWeblinkType & { translated_tw_weblink_type_name?: string }

const controllerNamePrefix = 'erp_2work.erp_2work.controller'
const deskNamePrefix = 'frappe.desk.doctype'
const utilsNamePrefix = 'frappe.utils'

// eslint-disable-next-line max-lines-per-function, max-statements
const useFrappeNestCustomAPIStore = defineStore('frappeNestCustomAPI', () => {
  const callAPIStore = useFrappeCallAPIStore()
  const dataAPIStore = useFrappeDataAPIStore()

  const getProjectBillingSummary = async (
    project: string,
  ): Promise<false | TWProjectBillingSummary> => {
    return await callAPIStore
      .post<ResponseWrapper<TWProjectBillingSummary>>(
        `${controllerNamePrefix}.conditions.project.billing_summary`,
        {
          project,
        },
      )
      .then((response) => {
        return response.data
      })
      // eslint-disable-next-line id-denylist
      .catch((error: unknown) => {
        console.error('getProjectBillingSummary Error:', error)
        return false
      })
  }

  const getDistributionListsForContact = async (
    contact: string,
  ): Promise<DistributionListMembership[]> => {
    return await callAPIStore
      .post<ResponseWrapper<DistributionListMembership[]>>(
        `${controllerNamePrefix}.subscriber.get_distribution_lists_for_contact`,
        {
          contact,
        },
      )
      .then((response) => {
        return response.data
      })

      .catch((exeption: unknown) => {
        console.error('getDistributionListsForContact Error:', exeption)
        return []
      })
  }

  const getDistributionListsForOrganization = async (
    doctype: string,
    name: string,
  ): Promise<DistributionListMembership[]> => {
    return await callAPIStore
      .post<ResponseWrapper<DistributionListMembership[]>>(
        `${controllerNamePrefix}.subscriber.get_distribution_lists_for_organization`,
        {
          doctype,
          name,
        },
      )
      .then((response) => {
        return response.data
      })

      .catch((exeption: unknown) => {
        console.error('getDistributionListsForOrganization Error:', exeption)
        return []
      })
  }

  const checkCanCreateProject = async (orderId: string): Promise<boolean> => {
    return await callAPIStore
      .post<ResponseWrapper<boolean>>(
        `${controllerNamePrefix}.conditions.sales_order.can_create_project`,
        {
          order_id: orderId,
        },
      )
      .then((response) => {
        return response.data
      })
      // eslint-disable-next-line id-denylist
      .catch((error: unknown) => {
        console.error('checkCanCreateProject Error:', error)
        return false
      })
  }

  const checkCanCreateDeliveryNote = async (orderId: string): Promise<boolean> => {
    return await callAPIStore
      .post<ResponseWrapper<boolean>>(
        `${controllerNamePrefix}.conditions.sales_order.can_create_delivery_note`,
        {
          order_id: orderId,
        },
      )
      .then((response) => {
        return response.data
      })
      // eslint-disable-next-line id-denylist
      .catch((error: unknown) => {
        console.error('checkCanCreateDeliveryNote Error:', error)
        return false
      })
  }

  const checkCanCreateSalesInvoiceFromSalesOrder = async (orderId: string): Promise<boolean> => {
    return await callAPIStore
      .post<ResponseWrapper<boolean>>(
        `${controllerNamePrefix}.conditions.sales_order.can_create_sales_invoice`,
        {
          order_id: orderId,
        },
      )
      .then((response) => {
        return response.data
      })
      // eslint-disable-next-line id-denylist
      .catch((error: unknown) => {
        console.error('checkCanCreateSalesInvoiceFromSalesOrder Error:', error)
        return false
      })
  }

  const checkCanCreateDunning = async (invoiceId: string): Promise<boolean> => {
    return await callAPIStore
      .post<ResponseWrapper<boolean>>(
        `${controllerNamePrefix}.conditions.sales_invoice.can_create_dunning`,
        {
          invoice_id: invoiceId,
        },
      )
      .then((response) => {
        return response.data
      })
      // eslint-disable-next-line id-denylist
      .catch((error: unknown) => {
        console.error('checkCanCreateDunning Error:', error)
        return false
      })
  }

  const checkCanCreatePayment = async (invoiceId: string): Promise<boolean> => {
    return await callAPIStore
      .post<ResponseWrapper<boolean>>(
        `${controllerNamePrefix}.conditions.sales_invoice.can_create_payment`,
        {
          invoice_id: invoiceId,
        },
      )
      .then((response) => {
        return response.data
      })
      // eslint-disable-next-line id-denylist
      .catch((error: unknown) => {
        console.error('checkCanCreatePayment Error:', error)
        return false
      })
  }

  const checkCanCreateCreditNoteFromSalesInvoice = async (invoiceId: string): Promise<boolean> => {
    return await callAPIStore
      .post<ResponseWrapper<boolean>>(
        `${controllerNamePrefix}.conditions.sales_invoice.can_create_credit_note`,
        {
          invoice_id: invoiceId,
        },
      )
      .then((response) => {
        return response.data
      })
      // eslint-disable-next-line id-denylist
      .catch((error: unknown) => {
        console.error('checkCanCreatePayment Error:', error)
        return false
      })
  }

  const checkCanCreateCreditNoteFromPurchaseInvoice = async (
    invoiceId: string,
  ): Promise<boolean> => {
    return await callAPIStore
      .post<ResponseWrapper<boolean>>(
        `${controllerNamePrefix}.conditions.purchase_invoice.can_create_credit_note`,
        {
          invoice_id: invoiceId,
        },
      )
      .then((response) => {
        return response.data
      })
      // eslint-disable-next-line id-denylist
      .catch((error: unknown) => {
        console.error('checkCanCreatePayment Error:', error)
        return false
      })
  }
  const checkCanCreatePaymentFromPurchaseInvoice = async (invoiceId: string): Promise<boolean> => {
    return await callAPIStore
      .post<ResponseWrapper<boolean>>(
        `${controllerNamePrefix}.conditions.purchase_invoice.can_create_payment`,
        {
          invoice_id: invoiceId,
        },
      )
      .then((response) => {
        return response.data
      })
      // eslint-disable-next-line id-denylist
      .catch((error: unknown) => {
        console.error('checkCanCreatePayment Error:', error)
        return false
      })
  }

  const checkDatevAttachFilesConfigured = async (doctype: string): Promise<boolean> => {
    return await callAPIStore
      .post<ResponseWrapper<boolean>>(
        `${controllerNamePrefix}.datev.check_datev_attach_files_configured`,
        { doctype },
      )
      .then((response) => response.data)
      // eslint-disable-next-line id-denylist
      .catch((error: unknown) => {
        console.error('checkDatevAttachFilesConfigured Error:', error)
        return false
      })
  }

  const getCurrentUserOnboardingStatus = async (): Promise<UserOnboardingStatusResponse> => {
    const response = await callAPIStore.get<ResponseWrapper<UserOnboardingStatusResponse>>(
      `${controllerNamePrefix}.user_onboarding.get_current_user_onboarding_status`,
      {},
    )
    return response.data
  }

  const completeCurrentUserOnboarding = async (
    tourVersion = ONBOARDING_TOUR_VERSION,
  ): Promise<UserOnboardingStatusResponse> => {
    const response = await callAPIStore.post<ResponseWrapper<UserOnboardingStatusResponse>>(
      `${controllerNamePrefix}.user_onboarding.complete_current_user_onboarding`,
      {
        tour_version: tourVersion,
      },
    )
    return response.data
  }

  const checkCanCreateAutoRepeat = async (doctype: string, docId: string): Promise<boolean> => {
    return await callAPIStore
      .post<ResponseWrapper<boolean>>(
        `${controllerNamePrefix}.conditions.auto_repeat.can_create_auto_repeat`,
        {
          doc_id: docId,
          doctype,
        },
      )
      .then((response) => {
        return response.data
      })
      // eslint-disable-next-line id-denylist
      .catch((error: unknown) => {
        console.error('checkCanCreateAutoRepeat Error:', error)
        return false
      })
  }

  const getTranslatedDocList = async <T>(doctype: string): Promise<T[]> => {
    const response = await callAPIStore.post<ResponseWrapper<T[]>>(
      `${controllerNamePrefix}.translation.get_translated_list`,
      {
        doctype,
        language: getCurrentLanguage(),
      },
    )

    return response.data
  }

  const getWebLinkTypes = async (): Promise<TranslatedTWWeblinkType[]> => {
    return await getTranslatedDocList<TranslatedTWWeblinkType>('TW Weblink Type')
  }

  const getPhoneTypes = async (): Promise<TranslatedTWPhoneType[]> => {
    return await getTranslatedDocList<TranslatedTWPhoneType>('TW Phone Type')
  }

  const getCountries = async (): Promise<Country[]> => {
    const countries: Country[] = await dataAPIStore.getDocList<Country>('Country', {
      fields: ['name', 'country_name'],
      limit: 0,
    })

    return countries
  }

  const getIndustryList = async (): Promise<IndustryType[]> => {
    const industryList: IndustryType[] = await dataAPIStore.getDocList<IndustryType>(
      'Industry Type',
      {
        fields: ['name', 'industry'],
        limit: 0,
      },
    )

    return industryList
  }

  const getCustomerGroupList = async (): Promise<
    { customer_group_name: string; name: string }[]
  > => {
    return await dataAPIStore.getDocList<{ customer_group_name: string; name: string }>(
      'Customer Group',
      {
        fields: ['name', 'customer_group_name'],
        limit: 0,
      },
    )
  }

  const getSupplierGroupList = async (): Promise<
    { name: string; supplier_group_name: string }[]
  > => {
    return await dataAPIStore.getDocList<{ name: string; supplier_group_name: string }>(
      'Supplier Group',
      {
        fields: ['name', 'supplier_group_name'],
        limit: 0,
      },
    )
  }

  const getSearchResults = async (
    searchString: string,
    doctype?: string,
  ): Promise<GlobalSearchResult[]> => {
    const searchResponse = await callAPIStore
      .post<GlobalSearchResponse>(`${utilsNamePrefix}.global_search.search`, {
        doctype,
        text: searchString,
      })
      .then((response) => {
        return response.message
      })
      .catch((searchError: unknown) => {
        // TODO: correct error handling in the view component
        console.error('getSearchResults Error: ', searchError)
        const fallback: GlobalSearchResult[] = []
        return fallback
      })

    return searchResponse
  }

  const getOrganizationList = async (
    organizationParameterList: GetDocsRequest,
  ): Promise<BRMOrganization[]> => {
    const organizationRequestParameters: Record<string, unknown> = {
      fields: organizationParameterList.fields ?? ['*'],
      filters: organizationParameterList.filters,
      limit: organizationParameterList.limit,
      limit_start: organizationParameterList.limit_start,
      order_by: organizationParameterList.order_by,
      relation_filters: organizationParameterList.relation_filters,
      sub_type: organizationParameterList.sub_type,
      type: RelationGroupType.organizations,
    }
    const organizationList = await callAPIStore
      .post<ResponseWrapper<BRMOrganization[]>>(
        `${controllerNamePrefix}.doc.get_list`,
        organizationRequestParameters,
      )
      .then((responseBRMOrganizationList) => {
        return responseBRMOrganizationList.data
      })
      .catch((getError: unknown) => {
        // TODO: correct error handling in the view component
        console.error('getOrganizationList Error: ', getError)

        const fallBackList: BRMOrganization[] = []

        return fallBackList
      })

    return organizationList
  }

  const getOrganizationDoc = async (
    organizationDoctype: string,
    organizationId: string,
  ): Promise<BRMOrganization | undefined> => {
    const organizationRequestParameters: Record<string, unknown> = {
      filters: { AND: [['name', '=', organizationId]], OR: [] },
      limit: 1,
      limit_start: 0,
      sub_type: organizationDoctype,
      type: RelationGroupType.organizations,
    }
    const organizationList = await callAPIStore
      .post<ResponseWrapper<BRMOrganization[]>>(
        `${controllerNamePrefix}.doc.get`,
        organizationRequestParameters,
      )
      .then((responseBRMOrganizationList) => {
        return responseBRMOrganizationList.data
      })
      .catch((getError: unknown) => {
        // TODO: correct error handling in the view component
        console.error('getOrganizationList Error: ', getError)

        const fallBackList: BRMOrganization[] = []

        return fallBackList
      })

    return organizationList[0]
  }

  const updateOrganizationList = async (
    organizationParameterList: UpdateBRMOrganizationDocument[],
  ): Promise<BRMOrganization[]> => {
    const updateRequestParameters: UpdateBRMOrganizationRequest = {
      documents: organizationParameterList,
    }

    const organizationUpdateList = await callAPIStore
      .post<ResponseWrapper<BRMOrganization[]>>(
        `${controllerNamePrefix}.doc.update`,
        updateRequestParameters,
      )
      .then((responseBRMOrganizationList) => {
        if (!(responseBRMOrganizationList.data.length > 0)) {
          // TODO: handle update error, when organization not found, backend responses with 200 and empty list
          throw new Error('updateOrganizationList Error: empty response')
        }

        return responseBRMOrganizationList.data
      })
      .catch((getError: unknown) => {
        // TODO: correct error handling in the view component
        console.error('updateOrganizationList Error: ', getError)

        const fallBackList: BRMOrganization[] = []

        return fallBackList
      })

    return organizationUpdateList
  }

  const getPersonDoc = async (personId: string): Promise<BRMPerson | undefined> => {
    const personRequestParameters: Record<string, unknown> = {
      filters: { AND: [['name', '=', personId]], OR: [] },
      limit: 1,
      limit_start: 0,
      sub_type: RelationType.all,
      type: RelationGroupType.persons,
    }
    const personList = await callAPIStore
      .post<ResponseWrapper<BRMPerson[]>>(
        `${controllerNamePrefix}.doc.get`,
        personRequestParameters,
      )
      .then((responseBRMPersonList) => {
        return responseBRMPersonList.data
      })
      .catch((getError: unknown) => {
        // TODO: correct error handling in the view component
        console.error('getPersonList Error: ', getError)

        const fallBackList: BRMPerson[] = []

        return fallBackList
      })

    return personList[0]
  }

  const getPersonList = async (personParameterList: GetDocsRequest): Promise<BRMPerson[]> => {
    const personRequestParameters: Record<string, unknown> = {
      fields: personParameterList.fields ?? ['*'],
      filters: personParameterList.filters,
      limit: personParameterList.limit,
      limit_start: personParameterList.limit_start,
      order_by: personParameterList.order_by,
      relation_filters: personParameterList.relation_filters,
      sub_type: personParameterList.sub_type,
      type: RelationGroupType.persons,
    }
    const personList = await callAPIStore
      .post<ResponseWrapper<BRMPerson[]>>(
        `${controllerNamePrefix}.doc.get_list`,
        personRequestParameters,
      )
      .then((responseBRMPersonList) => {
        return responseBRMPersonList.data
      })
      .catch((getError: unknown) => {
        // TODO: correct error handling in the view component
        console.error('getPersonList Error: ', getError)

        const fallBackList: BRMPerson[] = []

        return fallBackList
      })

    return personList
  }

  const updatePersonList = async (personParameterList: UpdateBRMPerson[]): Promise<BRMPerson[]> => {
    const updateRequestParameters: UpdatePersonRequest = {
      documents: personParameterList,
    }
    const personUpdateList = await callAPIStore
      .post<ResponseWrapper<BRMPerson[]>>(
        `${controllerNamePrefix}.doc.update`,
        updateRequestParameters,
      )
      .then((responseBRMOrganizationList) => {
        if (!(responseBRMOrganizationList.data.length > 0)) {
          // TODO: handle update error, when person not found, backend responses with 200 and empty list
          console.error('updatePersonList Error: Empty response')
        }

        return responseBRMOrganizationList.data
      })
      .catch((getError: unknown) => {
        // TODO: correct error handling in the view component
        console.error('updatePersonList Error: ', getError)

        const fallBackList: BRMPerson[] = []

        return fallBackList
      })

    return personUpdateList
  }

  const createPerson = async (person: Partial<BRMPerson>): Promise<BRMPerson> => {
    const payload = {
      documents: [person],
    }
    const response = await callAPIStore.post<ResponseWrapper<BRMPerson[]>>(
      `${controllerNamePrefix}.doc.create`,
      payload,
    )

    if (response.data.length === 0) {
      throw new Error('createPerson Error: Empty response')
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return response.data[0]!
  }

  const createOrganization = async (
    org: Partial<BRMOrganizationBase>,
  ): Promise<BRMOrganizationBase> => {
    const payload = {
      documents: [org],
    }
    const response = await callAPIStore.post<ResponseWrapper<BRMOrganizationBase[]>>(
      `${controllerNamePrefix}.doc.create`,
      payload,
    )

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return response.data[0]!
  }

  /**
   * Creates a new activity documentation record by sending the provided documentation data to the server.
   *
   * @param {ActivityDocumentationCreationRequest} newActivityDocumentation - The data required to create a new activity documentation.
   * @returns {Promise<ActivityDocumentationCreationResponse>} A promise that resolves to the response data of the created activity documentation.
   */
  const createActivityDocumentation = async (
    newActivityDocumentation: ActivityDocumentationCreationRequest,
  ): Promise<ActivityDocumentationCreationResponse> => {
    const payload = {
      documents: [newActivityDocumentation],
    }
    const response = await callAPIStore.post<
      ResponseWrapper<ActivityDocumentationCreationResponse>
    >(`${controllerNamePrefix}.doc.create`, payload)

    return response.data
  }

  /**
   * Asynchronously creates a new event by sending the provided event data to the API.
   *
   * @param {TWEventCreation} newEvent - The event data to be created.
   * @returns {Promise<EventCreationResponse>} A promise that resolves with the response containing the event creation details.
   */
  const createEvent = async (newEvent: TWEventCreation): Promise<EventCreationResponse> => {
    const payload = {
      documents: [newEvent],
    }
    const response = await callAPIStore.post<ResponseWrapper<EventCreationResponse>>(
      `${controllerNamePrefix}.doc.create`,
      payload,
    )

    return response.data
  }

  /**
   * Asynchronously creates a new email by sending the provided email data to the API.
   *
   * @param {CommunicationCreation} newEmail - The email data to be created.
   * @returns {Promise<EventCreationResponse>} A promise that resolves with the response containing the email creation details.
   */
  const createEmail = async (newEmail: CommunicationCreation): Promise<EventCreationResponse> => {
    const payload = {
      documents: [newEmail],
    }
    const response = await callAPIStore.post<ResponseWrapper<EventCreationResponse>>(
      `${controllerNamePrefix}.doc.create`,
      payload,
    )

    return response.data
  }

  /**
   * Updates an activity documentation item with the given data.
   *
   * @param {ActivityDocumentationUpdateRequest} updateActivityDocumentationItem - The activity documentation data to be updated.
   * @returns {Promise<TWActivityDocumentation>} A promise resolving to the updated activity documentation.
   */
  const updateActivityItem = async (
    updateActivityDocumentationItem: ActivityDocumentationUpdateRequest,
  ): Promise<TWActivityDocumentation> => {
    const payload = {
      documents: [updateActivityDocumentationItem],
    }
    const response = await callAPIStore.put<ResponseWrapper<TWActivityDocumentation>>(
      `${controllerNamePrefix}.doc.update`,
      payload,
    )

    console.debug('updateActivityItem response', response)

    return response.data
  }

  /**
   * Updates an existing event item with the provided data.
   *
   * This asynchronous function sends an updated event request encapsulated
   * within a payload to the specified API endpoint for processing. Upon
   * success, it returns the updated event data.
   *
   * @param {EventUpdateRequest} updateEventItem - The event update request containing the new data for the event item.
   * @returns {Promise<TWEvent>} A promise that resolves to the updated event object.
   * @throws {Error} If the API call fails or an unexpected response is received.
   */
  const updateEventItem = async (updateEventItem: EventUpdateRequest): Promise<TWEvent> => {
    const payload = {
      documents: [updateEventItem],
    }
    const response = await callAPIStore.put<ResponseWrapper<TWEvent>>(
      `${controllerNamePrefix}.doc.update`,
      payload,
    )

    return response.data
  }

  /**
   * Updates an existing event item with the provided data.
   *
   * This asynchronous function sends an updated event request encapsulated
   * within a payload to the specified API endpoint for processing. Upon
   * success, it returns the updated event data.
   *
   * @param {EmailUpdateRequest} updateEmailItem - The email update request containing the new data for the email item.
   * @returns {Promise<Communication>} A promise that resolves to the updated email object.
   * @throws {Error} If the API call fails or an unexpected response is received.
   */
  const updateEmailItem = async (updateEmailItem: EmailUpdateRequest): Promise<Communication> => {
    const payload = {
      documents: [updateEmailItem],
    }
    const response = await callAPIStore.put<ResponseWrapper<Communication>>(
      `${controllerNamePrefix}.doc.update`,
      payload,
    )

    return response.data
  }

  /**
   * Retrieves an activity entry based on the provided activity request.
   *
   * @param {ActivityRequest} activityRequest - The request object containing the details needed to fetch the activity entry.
   * @returns {Promise<ActivityDocumentationResponse | EventResponse>} A promise that resolves to either an ActivityDocumentationResponse or an EventResponse, depending on the requested activity.
   */
  const getActivityEntry = async (
    activityRequest: ActivityRequest,
  ): Promise<ActivityDocumentationResponse | EmailResponse | EventResponse> => {
    const response = await callAPIStore.post<
      ResponseWrapper<ActivityDocumentationResponse | EmailResponse | EventResponse>
    >(`${controllerNamePrefix}.activity.get_entry`, activityRequest)

    return response.data
  }

  const getDocListEmployee = async (
    fields?: EmployeeListParameterList,
    filters?: FilterConditionSingleValue[],
  ): Promise<EmployeeDynamic[]> => {
    const response = await callAPIStore.get<ResponseWrapper<EmployeeDynamic[]>>(
      `${controllerNamePrefix}.employee.get_employees_light`,
      {
        fields: fields ?? ['*'],
        filters: filters ?? [['employee_name', '!=', '']],
      },
    )

    return response.data
  }

  const getDocumentActivityContext = async (
    targetDocument: DocIdentifier,
  ): Promise<{
    organization: null | { doctype: string; name: string }
    person: null | { doctype: string; name: string }
  }> => {
    const response = await callAPIStore.post<
      ResponseWrapper<{
        organization: null | { doctype: string; name: string }
        person: null | { doctype: string; name: string }
      }>
    >(`${controllerNamePrefix}.activity.get_document_activity_context`, {
      docname: targetDocument.name,
      doctype: targetDocument.doctype,
    })
    return response.data
  }

  const getActivityDocumentationList = async (): Promise<TWActivityDocumentationDynamic[]> => {
    const activityDocumentationList = await dataAPIStore.getDocList<TWActivityDocumentation>(
      'TW Activity Documentation',
      {
        fields: ['*'],
      },
    )

    return activityDocumentationList
  }

  const getDocListDynamicLink = async (
    filters?: FilterConditionSingleValue[],
  ): Promise<DynamicLink[]> => {
    const defaultfilter: FilterConditionSingleValue[] = [['link_name', '=', '']]

    const dynamicLinkList = await dataAPIStore.getDocList<DynamicLink>('Dynamic Link', {
      filters: filters ?? defaultfilter,
    })

    return dynamicLinkList
  }

  // const getDocListUser = async (fields?: UserListParameterList): Promise<UserDynamic[]> => {
  //   const defaultUserParameter: UserListParameterList = ['name', 'username']
  //   const userList = await callAPIStore.callInstanceAxiosInstance.get<ResponseWrapper<UserDynamic[]>>(
  //     '/api/resource/User',
  //     {
  //       params: {
  //         fields: fields ?? defaultUserParameter,
  //       },
  //     },
  //   )

  //   return userList.data.data
  // }
  const getDocListUser = async (fields?: UserListParameterList): Promise<UserDynamic[]> => {
    const defaultUserParameter: UserListParameterList = ['name', 'username']
    const userList = await dataAPIStore.getDocList<UserDynamic>('User', {
      fields: fields ?? defaultUserParameter,
      filters: [['enabled', '=', 1]],
      limit: 0,
    })

    return userList
  }

  const getDocListAddress = async (
    filters?: [string, string, FilterConditionOperator, string | string[]][],
  ): Promise<AddressDynamic[]> => {
    const defaultfilter = [['Dynamic Link', 'link_name', '=', '']]

    const addressList = await callAPIStore.callInstanceAxiosInstance.get<
      ResponseWrapper<AddressDynamic[]>
    >('/api/resource/Address', {
      params: {
        fields: ['name'],
        filters: JSON.stringify(filters ?? defaultfilter),
      },
    })

    return addressList.data.data
  }

  const getAddress = async (addressName: string): Promise<AddressDynamic> => {
    const address = await callAPIStore.callInstanceAxiosInstance.get<
      ResponseWrapper<AddressDynamic>
    >(`/api/resource/Address/${addressName}`, {
      params: {
        fields: ['*'],
      },
    })

    return address.data.data
  }

  const getPerson = async (personName: string): Promise<Person> => {
    // TODO: needs ["Person (required fields)" <-> Contact] mapping
    // see types/external/2work/common.ts
    const person: Person = await dataAPIStore.getDoc<Contact>('Contact', personName)

    return person
  }

  const getAllContacts = async (): Promise<Contact[]> => {
    const contacts: Contact[] = await dataAPIStore.getDocList<Contact>('Contact', {
      fields: ['*'],
      limit: 0,
    })

    return contacts
  }

  const toggleFavoriteState = async (
    favoriteParameter: ToggleFavoriteStateRequest,
  ): Promise<FavoriteToggle> => {
    const response: ResponseWrapper<FavoriteToggle> = await callAPIStore.put(
      `${controllerNamePrefix}.favorite.toggle_state`,
      favoriteParameter,
    )

    return response.data
  }

  const toggleLiked = async (
    doctype: string,
    name: string,
    add: 'No' | 'Yes',
  ): Promise<unknown> => {
    const response = await callAPIStore.put('frappe.desk.like.toggle_like', {
      add,
      doctype,
      name,
    })

    return response
  }

  const updateRatingsByDoctype = async (
    getRatingsParams: GetRatingsRequest,
  ): Promise<RatingReponse[]> => {
    const ratingResponseObject = await callAPIStore.post<ResponseWrapper<RatingReponse[]>>(
      `${controllerNamePrefix}.rating.get`,
      {
        ...getRatingsParams,
        language: getCurrentLanguage(),
      },
    )
    const newRatingList: RatingReponse[] = ratingResponseObject.data

    return newRatingList
  }

  const addTagsToDocuments = async (
    addTagsToDocumentsObject: TagAdditionBulkRequest,
  ): Promise<TagAddition[]> => {
    const response = await callAPIStore.post<ResponseWrapper<TagAddition[]>>(
      `${controllerNamePrefix}.doc.add_tags_to_documents`,
      addTagsToDocumentsObject,
    )

    return response.data
  }

  const removeTagsFromDocument = async (
    doctype: string,
    docname: string,
    tag: string,
  ): Promise<void> => {
    await callAPIStore.post<ResponseWrapper<never>>(`${deskNamePrefix}.tag.tag.remove_tag`, {
      dn: docname,
      dt: doctype,
      tag,
    })
  }

  const fetchKPIData = async (request: GetKPIDataRequest): Promise<KPIData> => {
    const response = await callAPIStore.post<ResponseWrapper<KPIData>>(
      `${controllerNamePrefix}.kpi.get`,
      request,
    )

    return response.data
  }

  const getCompletionRate = async (
    request: GetCompletionRateDataRequest,
  ): Promise<GetCompletionRateResponse> => {
    const response = await callAPIStore.post<ResponseWrapper<GetCompletionRateResponse>>(
      `${controllerNamePrefix}.completion_rate.get`,
      request,
    )

    return response.data
  }

  const updatePerson = async (personDocName: string, person: Partial<Person>): Promise<Person> => {
    const response: Person = await dataAPIStore.updateDoc<Person>('Contact', personDocName, person)

    console.debug('updatePerson response', response)

    return response
  }

  const updateAddress = async (
    addressDocName: string,
    address: Partial<Address>,
  ): Promise<Address> => {
    const response = await dataAPIStore.updateDoc<Address>('Address', addressDocName, address)

    return response
  }

  const createAddress = async (address: AddressCreation): Promise<AddressDynamic> => {
    const response = await dataAPIStore.createDoc<AddressCreation>('Address', address)

    return response
  }

  /**
   * Get all tags from the backend.
   * @returns A string list containing all avaiable tags.
   */
  const getAllTags = async (): Promise<string[]> => {
    const tagResponseObject = await callAPIStore.get<ResponseWrapper<string[]>>(
      `${controllerNamePrefix}.tag.get`,
      {},
    )
    const tagList: string[] = tagResponseObject.data

    return tagList
  }

  const getTagsforTypes = async (relationIdentifier: RelationIdentifier): Promise<string[]> => {
    const tagResponseObject = await callAPIStore.post<ResponseWrapper<string[]>>(
      `${controllerNamePrefix}.tag.get_for_types`,
      relationIdentifier,
    )

    return tagResponseObject.data
  }

  const getDoctypeActivityFeed = async (
    targetDoctypeRequest: DoctypeTargetActivityFeedRequest,
  ): Promise<DoctypeTargetActivityFeedResponse> => {
    const response = await callAPIStore.post<ResponseWrapper<DoctypeTargetActivityFeedResponse>>(
      `${controllerNamePrefix}.activity.get_feed`,
      targetDoctypeRequest,
    )

    return response.data
  }

  const getDoctypeRelations = async (
    targetDoctypeRequest: DoctypeRelationsRequest,
  ): Promise<DoctypeRelationsResponse[]> => {
    const response = await callAPIStore.post<ResponseWrapper<DoctypeRelationsResponse[]>>(
      `${controllerNamePrefix}.doc.get_relations`,
      targetDoctypeRequest,
    )

    return response.data
  }

  const getDoctypeTargetRelations = async (
    targetDoctypeRequest: DoctypeTargetRelationsRequest,
  ): Promise<DoctypeTargetRelationsResponse> => {
    const targetDoctypeSystemRequest: DoctypeTargetRelationsSystemRequest = {
      ...targetDoctypeRequest,
      combined: 1,
    }

    const response = await callAPIStore.post<ResponseWrapper<DoctypeTargetRelationsResponse>>(
      `${controllerNamePrefix}.doc.get_relations`,
      targetDoctypeSystemRequest,
    )

    return response.data
  }

  const getDoctypeItems = async (
    doctype: string,
    searchTerm?: string,
    limit = 5,
    translateTitles = false,
  ): Promise<{ name: string; title: string }[]> => {
    const response = await callAPIStore.post<ResponseWrapper<{ name: string; title: string }[]>>(
      `${controllerNamePrefix}.doc.get_doctype_items`,
      {
        doctype,
        limit,
        search_term: searchTerm,
        translate_titles: translateTitles,
      },
    )
    return response.data
  }

  const getKanbanStatesByDoctype = async (
    request: GetKanbanStatesRequest,
  ): Promise<GetKanbanStatesResponse[]> => {
    const response = await callAPIStore.post<ResponseWrapper<GetKanbanStatesResponse[]>>(
      `${controllerNamePrefix}.kanban.get_states_for_types`,
      request,
    )

    return response.data
  }

  const deleteDocuments = async (
    docDeletionBulkRequest: DocDeletionBulkRequest,
    withMessageOutput = false,
  ): Promise<boolean | null | { errorMessage: string } | { message: string }> => {
    const response = await callAPIStore.post<ResponseWrapper<DocDeletion[]>>(
      `${controllerNamePrefix}.doc.delete_documents`,
      docDeletionBulkRequest,
    )

    console.debug('deleteDocuments response', response)
    const errorEntry = response.data.find((doc) => doc.errorMessage)
    const messageEntry = response.data.find((doc) => doc.message)

    if (withMessageOutput) {
      if (errorEntry?.errorMessage) return { errorMessage: errorEntry.errorMessage }
      if (messageEntry?.message) return { message: messageEntry.message }
      return null
    }
    const hasMessage = !!(errorEntry ?? messageEntry)

    if (messageEntry) console.debug('deleteDocuments hasMessage', hasMessage)

    return hasMessage
  }

  const getRoles = async (): Promise<TranslatedTWRole[]> => {
    return await getTranslatedDocList<TranslatedTWRole>('TW Role')
  }

  const getOrganizationAIInfo = async (
    organizationName: string,
    options?: {
      available_countries?: { label: string; value: string }[]
      available_phone_types?: { label: string; value: string }[]
      available_weblink_types?: { label: string; value: string }[]
    },
  ): Promise<null | TWOrganizationAIResponse> => {
    try {
      if (!organizationName.trim()) {
        console.warn('getOrganizationAIInfo: missing organizationName')
        return null
      }
      const response = await callAPIStore.post<ResponseWrapper<TWOrganizationAIResponse>>(
        'erp_2work.erp_2work.controller.ai.get_company_info',
        {
          organization_name: organizationName.trim(),
          ...options,
        },
      )
      return response.data
      // eslint-disable-next-line id-denylist
    } catch (error) {
      console.error('getOrganizationAIInfo Error:', error)
      return null
    }
  }

  const getCompanyManagementAI = async (
    request: CompanyManagementRequest,
  ): Promise<CompanyManagementResponse> => {
    const response = await callAPIStore.post<ResponseWrapper<CompanyManagementResponse>>(
      'erp_2work.erp_2work.controller.ai.get_company_management',
      { ...request, organization_name: request.organization_name.trim() },
    )
    return response.data
  }

  const findManagementPersonMatches = async (
    request: ManagementPersonMatchesRequest,
  ): Promise<ManagementPersonMatches> => {
    const response = await callAPIStore.post<ResponseWrapper<ManagementPersonMatches>>(
      'erp_2work.erp_2work.controller.ai.find_management_person_matches',
      request,
    )
    return response.data
  }

  const applyManagementSuggestions = async (
    request: ManagementApplyRequest,
  ): Promise<ManagementApplyResponse> => {
    const response = await callAPIStore.post<ResponseWrapper<ManagementApplyResponse>>(
      'erp_2work.erp_2work.controller.ai.apply_management_suggestions',
      request,
    )
    return response.data
  }

  const generateAIEmailText = async (
    description: string,
    options?: {
      attachments?: string[] | undefined
      organizations?: string[] | undefined
      persons?: string[] | undefined
      references?: string[] | undefined
      subject?: string | undefined
    },
  ): Promise<null | string> => {
    try {
      if (!description.trim()) {
        console.warn('generateAIEmailText: missing description')
        return null
      }
      const response = await callAPIStore.post<ResponseWrapper<{ text: string }>>(
        `${controllerNamePrefix}.ai.generate_email_text`,
        {
          description: description.trim(),
          ...options,
        },
      )
      return response.data.text
    } catch (exception) {
      console.error('generateAIEmailText Error:', exception)
      return null
    }
  }

  const getPaymentTermsTemplates = async (): Promise<PaymentTermsTemplate[]> => {
    return await dataAPIStore.getDocList<PaymentTermsTemplate>('Payment Terms Template', {})
  }

  const getTWInvestorType = async (): Promise<TWInvestorType[]> => {
    return await dataAPIStore.getDocList<TWInvestorType>('TW Investor Type', {})
  }

  const getTWOtherType = async (): Promise<TWOtherType[]> => {
    return await dataAPIStore.getDocList<TWOtherType>('TW Other Type', {})
  }

  /**
   * Get reports for ref doctype.
   * **/
  const getReportsForRefDoctype = async (
    refDoctype: string,
    relationshipType?: string,
  ): Promise<null | ReportRefItem[]> => {
    try {
      const filters: [string, '=' | 'like', number | string][] = [
        ['disabled', '=', 0],
        ['is_standard', '=', 'No'],
        ['report_type', '=', 'Report Builder'],
        ['ref_doctype', '=', refDoctype],
      ]

      if (relationshipType) {
        filters.push(['json', 'like', `%"relationship_type":"${relationshipType}"%`])
      }

      return await dataAPIStore.getDocList('Report', {
        fields: ['name', 'owner'],
        filters,
        limit: 1000,
      })
    } catch (catchError) {
      console.error('catch Error:', catchError)
      return null
    }
  }

  const getKanbanBoardsForRefDoctype = async (
    refDoctype: string,
  ): Promise<KanbanBoardRefItem[] | null> => {
    try {
      const rows = await dataAPIStore.getDocList<KanbanBoardRefItem>('Kanban Board', {
        fields: ['name', 'owner', 'private'],
        filters: [['reference_doctype', '=', refDoctype]],
        limit: 1000,
      })

      return [...rows].sort((firstBoard, secondBoard) => {
        const defaultBoardName = getDefaultKanbanBoardName(refDoctype)

        if (firstBoard.name === defaultBoardName) return -1
        if (secondBoard.name === defaultBoardName) return 1

        return firstBoard.name.localeCompare(secondBoard.name)
      })
    } catch (catchError) {
      console.error('catch Error:', catchError)
      return null
    }
  }

  const setKanbanBoardPrivateState = async (
    kanbanBoardName: string,
    privateState: boolean,
  ): Promise<boolean> => {
    try {
      await dataAPIStore.updateDoc<KanbanBoardRefItem>('Kanban Board', kanbanBoardName, {
        private: privateState ? 1 : 0,
      })
      return true
    } catch (catchError) {
      console.error('catch Error:', catchError)
      return false
    }
  }

  const setPermissionForAllUsersForOneDocument = async (
    doctype: string,
    reportName: string,
    permission: ReportPermission,
    value: ShareValue,
  ): Promise<boolean> => {
    try {
      const payload = {
        doctype,
        everyone: 1,
        name: reportName,
        permission_to: permission,
        user: null,
        value,
      }
      await callAPIStore.post('frappe.share.set_permission', payload)
      return true
    } catch (catchError) {
      console.error('catch Error:', catchError)
      return false
    }
  }
  const getShareUsers = async (doctype: string, name: string): Promise<shareUser[]> => {
    try {
      const payload = {
        doctype,
        name,
      }
      const response = await callAPIStore.post<getShareUsersResponse>(
        'frappe.share.get_users',
        payload,
      )
      return response.message
    } catch (catchError) {
      console.error('catch Error:', catchError)
      return []
    }
  }

  /**
   * The API-Call is used for the bell icon in NotificationMenu.vue. It displays the tasks and notifications
   *     of the logged-in user in a nice dropdown view that is shown on every page of the project. When scrolling,
   *     additional entries are loaded to improve performance by reducing the amount of data. Currently, this API
   *     is additionally called every few seconds via polling for updates. TODO: Eventually switch to webhook instead of polling.
   *
   * @param pa
   */
  const getUnreadDashboard = async (pa: {
    notif_limit: number
    notif_offset: number
    todo_limit: number
    todo_offset: number
  }): Promise<false | NotificationDashboards> => {
    try {
      const response = await callAPIStore.post<ResponseWrapper<NotificationDashboards>>(
        `${controllerNamePrefix}.notifications.get_unread_dashboard`,
        pa,
      )
      return response.data
    } catch (errMsg) {
      console.error('getUnreadDashboard Error:', errMsg)
      return false
    }
  }

  /**
   * The API-Call is used for the bell icon in NotificationMenu.vue.
   * It marks the notification as read.
   * @param name
   */
  const markAsRead = async (name: string): Promise<boolean> => {
    try {
      const response = await callAPIStore.post<ResponseWrapper<PostMarkAsRead>>(
        `${controllerNamePrefix}.notifications.mark_as_read`,
        { docname: name },
      )
      return !!response.data.success
    } catch (errMsg) {
      console.error('getUnreadDashboard Error:', errMsg)
      return false
    }
  }

  /**
   * The API-Call is used for the envelope icon in CommunicationsMenu.vue. It displays the unread communications
   *     of the logged-in user that is shown on every page of the project.
   *     It is called every few seconds via polling for updates.
   */
  const getUnreadCommunications = async (): Promise<false | GetUnreadCommunicationsResponse> => {
    try {
      const response = await callAPIStore.get<ResponseWrapper<GetUnreadCommunicationsResponse>>(
        `${controllerNamePrefix}.communication.get_unread_communications_count`,
        {},
      )
      return response.data
    } catch (errMsg) {
      console.error('getUnreadCommunications Error:', errMsg)
      return false
    }
  }

  /**
   * The API-Call is used to fetch the email accounts of the logged-in user for showing the available inboxes.
   *    It is called when the user opens the email inbox view.
   */
  const getUserEmailAccounts = async (): Promise<false | GetEmailInboxesResponse> => {
    try {
      const response = await callAPIStore.get<ResponseWrapper<GetEmailInboxesResponse>>(
        `${controllerNamePrefix}.communication.get_user_email_accounts`,
        {},
      )
      return response.data
    } catch (errMsg) {
      console.error('getUserEmailAccounts Error:', errMsg)
      return false
    }
  }

  /**
   * Send the welcome (invitation) email of a user again. The mail carries a freshly generated
   * set-password link, which invalidates the one sent before. Authenticated endpoint.
   */
  const resendUserInvitation = async (user: string): Promise<void> => {
    await callAPIStore.post<ResponseWrapper<never>>(
      `erp_2work.erp_2work.component.override.TWorkUser.resend_welcome_email`,
      { user },
    )
  }

  /**
   * The API-Call is used to reset the user password when the user forgot his password.
   */
  const resetUserPassword = async (user: string): Promise<ResetUserPasswordResponse> => {
    // Get the current language, to get translated error messages from frappe
    const currentLanguage = getCurrentLanguage()

    return await callAPIStore.post<ResetUserPasswordResponse>(
      `frappe.core.doctype.user.user.reset_password`,
      { _lang: currentLanguage, user },
    )
  }

  /**
   * The API-Call is used to update the user password.
   */
  const updateUserPassword = async (
    updatePasswordRequest: UpdatePasswordRequest,
  ): Promise<UserAuthResponse> => {
    // Get the current language, to get translated error messages from frappe
    const currentLanguage = getCurrentLanguage()

    return await callAPIStore.post<UserAuthResponse>(
      `frappe.core.doctype.user.user.update_password`,
      {
        _lang: currentLanguage,
        confirm_password: updatePasswordRequest.confirmPassword,
        key: updatePasswordRequest.key,
        logout_all_sessions: true,
        new_password: updatePasswordRequest.newPassword,
      },
    )
  }

  /**
   * The API-Call is used to get the subscribed newsletters for a given email address.
   */
  const getSubscribedNewsletters = async (
    getSubscribedNewslettersRequest: GetSubscribedNewslettersRequest,
  ): Promise<GetSubscribedNewslettersResponse> => {
    return await callAPIStore.get<GetSubscribedNewslettersResponse>(
      `erp_2work.erp_2work.controller.email_group.get_subscribed_email_groups?email=${getSubscribedNewslettersRequest.email}&doctype=${getSubscribedNewslettersRequest.doctype}&name=${getSubscribedNewslettersRequest.name}&_signature=${getSubscribedNewslettersRequest.signature}`,
      {},
    )
  }

  /**
   * The API-Call is used to unsubscribe from newsletter distribution lists.
   */
  const unsubscribeFromNewsletters = async (
    unsubscribeRequest: UnsubscribeFromNewslettersRequest,
  ): Promise<{ status: string }> => {
    return await callAPIStore.post<{ status: string }>(
      `erp_2work.erp_2work.controller.email_group.unsubscribe`,
      {
        email: unsubscribeRequest.email,
        email_groups: unsubscribeRequest.emailGroups,
        token: unsubscribeRequest.token,
      },
    )
  }

  /**
   * Suggest possible signers (contact / primary contact) for a quotation. Authenticated endpoint.
   */
  const getSuggestedSigners = async (
    referenceDoctype: string,
    referenceName: string,
  ): Promise<SuggestedSignersResponse> => {
    return await callAPIStore.get<SuggestedSignersResponse>(
      `erp_2work.erp_2work.controller.signature.get_suggested_signers?reference_doctype=${encodeURIComponent(referenceDoctype)}&reference_name=${encodeURIComponent(referenceName)}`,
      {},
    )
  }

  /**
   * Start a signature process from a document: fix the PDF and email each signer their own
   * secure signing link. Authenticated endpoint (write permission on the document).
   */
  const createAndSendSignature = async (
    createRequest: CreateSignatureRequest,
  ): Promise<CreateSignatureResponse> => {
    return await callAPIStore.post<CreateSignatureResponse>(
      `erp_2work.erp_2work.controller.signature.create_and_send`,
      {
        cc_emails: createRequest.ccEmails,
        expires_at: createRequest.expiresAt,
        include_self: createRequest.includeSelf,
        message: createRequest.message,
        notify_by_email: createRequest.notifyByEmail,
        notify_by_system: createRequest.notifyBySystem,
        reference_doctype: createRequest.referenceDoctype,
        reference_name: createRequest.referenceName,
        signers: createRequest.signers.map((signer) => ({
          email: signer.email,
          name: signer.name,
          role: signer.role ?? '',
        })),
        subject: createRequest.subject,
        use_signing_order: createRequest.useSigningOrder,
      },
    )
  }

  /**
   * List the signature requests of a quotation (newest first). Authenticated endpoint.
   */
  const getSignatureRequests = async (
    referenceDoctype: string,
    referenceName: string,
  ): Promise<SignatureRequestListResponse> => {
    return await callAPIStore.get<SignatureRequestListResponse>(
      `erp_2work.erp_2work.controller.signature.get_signature_requests?reference_doctype=${encodeURIComponent(referenceDoctype)}&reference_name=${encodeURIComponent(referenceName)}`,
      {},
    )
  }

  /**
   * Abort a pending signature request; the signing link becomes invalid. Authenticated endpoint.
   */
  const cancelSignatureRequest = async (name: string): Promise<void> => {
    await callAPIStore.post<ResponseWrapper<never>>(
      `erp_2work.erp_2work.controller.signature.cancel_signature_request`,
      { name },
    )
  }

  /**
   * Send the invitation of the current signing step again. Every targeted signer receives a
   * freshly generated link, which invalidates the one mailed before. Without `signerEmails`
   * all signers of the current step are notified. Authenticated endpoint.
   */
  const resendSignatureRequest = async (name: string, signerEmails: string[]): Promise<void> => {
    await callAPIStore.post<ResponseWrapper<never>>(
      `erp_2work.erp_2work.controller.signature.resend_signature_request`,
      { name, signer_emails: signerEmails },
    )
  }

  /**
   * Fetch the public signing context (quotation label, signer, PDF url) for a signature token.
   * Guest endpoint, no authentication required. POST so the secret token stays out of the URL.
   */
  const getSignatureRequest = async (token: string): Promise<SignatureRequestResponse> => {
    return await callAPIStore.post<SignatureRequestResponse>(
      `erp_2work.erp_2work.controller.signature.get_signature_request`,
      { token },
    )
  }

  /**
   * Submit a drawn signature for the given token. Guest endpoint, no authentication required.
   */
  const submitSignature = async (
    submitRequest: SignatureSubmitRequest,
  ): Promise<SignatureSubmitResponse> => {
    return await callAPIStore.post<SignatureSubmitResponse>(
      `erp_2work.erp_2work.controller.signature.submit_signature`,
      {
        consent: submitRequest.consent,
        location: submitRequest.location,
        signature_png: submitRequest.signaturePng,
        token: submitRequest.token,
      },
    )
  }

  /**
   * The API-Call is used to accept a subscription via token.
   */
  const acceptSubscription = async (token: string): Promise<{ message: { success: boolean } }> => {
    // Get the current language, to get translated error messages from frappe
    const currentLanguage = getCurrentLanguage()

    return await callAPIStore.post<{ message: { success: boolean } }>(
      `erp_2work.erp_2work.controller.subscriber.accept_subscription`,
      {
        _lang: currentLanguage,
        token,
      },
    )
  }

  const createNewPrintFormat = async (payload: PrintFormatCreation): Promise<PrintFormat> => {
    return await dataAPIStore.createDoc<PrintFormatCreation>('Print Format', payload)
  }

  const createPrintDesignerFormat = async (
    docType: string,
    name: string,
  ): Promise<{ name: string }> => {
    const response = await callAPIStore.post<{ message: { name: string } }>(
      'erp_2work.public.py.print_format_methods.create_print_designer_format',
      {
        doc_type: docType,
        print_format_name: name,
      },
    )

    return response.message
  }

  const duplicatePrintDesignerFormat = async (
    sourceName: string,
    name: string,
  ): Promise<{ name: string }> => {
    const response = await callAPIStore.post<{ message: { name: string } }>(
      'erp_2work.public.py.print_format_methods.duplicate_print_designer_format',
      {
        print_format_name: name,
        source_name: sourceName,
      },
    )

    return response.message
  }

  /**
   * The API-Call is used to get the necessary information for setting up two-factor authentication, such as the user's first name and a QR code in SVG format that can be scanned by an authenticator app.
   */
  const getTwoFactorAuthSetup = async (
    token: string,
  ): Promise<{ message: { first_name: string; qr_code_svg: string } }> => {
    return await callAPIStore.get<{ message: { first_name: string; qr_code_svg: string } }>(
      `erp_2work.erp_2work.controller.two_factor_auth.get_two_factor_auth_setup`,
      {
        // "k" is too short, but frappe expects the token parameter to be named "k"
        // eslint-disable-next-line id-length
        k: token,
      },
    )
  }

  /**
   * Fetches a list of calendar events based on the specified filters.
   *
   * @param {string} starts_on - The start date for fetching events in a string format (e.g., "YYYY-MM-DD").
   * @param {string} ends_on - The end date for fetching events in a string format (e.g., "YYYY-MM-DD").
   * @param {boolean|number} [only_my_events=false] - When true or set to 1, fetches only events owned by the user.
   * @param {null|string|string[]} [google_calendars=null] - Specifies one or more Google calendar IDs to filter events. Can be null, a single calendar ID string, or an array of calendar IDs.
   * @param {boolean|number} [include_system_events=true] - When true or set to 1, includes system-generated events in the results.
   * @returns {Promise<CalendarEvent[]>} - A promise that resolves to an array of calendar events matching the specified filters.
   */
  const getCalendarEvents = async (
    starts_on: string,
    ends_on: string,
    only_my_events: boolean | number = false,
    google_calendars: null | string | string[] = null,
    include_system_events: boolean | number = true,
  ): Promise<CalendarEvent[]> => {
    const response = await callAPIStore.post<ResponseWrapper<CalendarEvent[]>>(
      `${controllerNamePrefix}.calendar.get_events`,
      {
        ends_on,
        google_calendars,
        include_system_events,
        only_my_events,
        starts_on,
      },
    )

    return response.data
  }

  /**
   * @returns {Promise<UserCalendarEvent[]>} A promise that resolves with an array of
   * user calendar events.
   */
  const getUserCalendars = async (): Promise<UserCalendarEvent[]> => {
    const response = await callAPIStore.get<ResponseWrapper<UserCalendarEvent[]>>(
      `${controllerNamePrefix}.calendar.get_user_calendars`,
      {},
    )

    return response.data
  }

  const getBankAccountList = async (
    fields?: (keyof BankAccount)[],
    filters?: FilterConditionSingleValue[],
  ): Promise<BankAccount[]> => {
    return await dataAPIStore.getDocList<BankAccount>('Bank Account', {
      fields: fields ?? [
        'name',
        'account_name',
        'branch_code',
        'party',
        'party_type',
        'iban',
        'is_default',
        'disabled',
        'bank',
      ],
      filters: filters ?? [],
    })
  }

  const updateBankAccount = async (
    docname: string,
    value: Partial<BankAccount>,
  ): Promise<BankAccount> => {
    return await dataAPIStore.updateDoc<BankAccount>('Bank Account', docname, value)
  }

  const createBankAccount = async (value: BankAccount): Promise<BankAccount> => {
    return await dataAPIStore.createDoc<BankAccount>('Bank Account', value)
  }

  const getBankList = async (
    fields?: (keyof BankDynamic)[],
    filters?: FilterConditionSingleValue[],
  ): Promise<BankDynamic[]> => {
    return await dataAPIStore.getDocList<BankDynamic>('Bank', {
      fields: fields ?? ['name', 'bank_name'],
      filters: filters ?? [],
    })
  }

  /**
   * Asynchronously retrieves a list of companies based on the provided payload.
   *
   * This function interacts with the dataAPIStore to fetch the data. The payload
   * contains filtering and query parameters used to retrieve the relevant
   * company data.
   *
   * @param {GetDocListArgs} payload - The parameters used for filtering and fetching
   *                                   the company list.
   * @returns {Promise<Company[]>} A promise resolving to an array of Company objects.
   */
  const getCompanyList = async (payload: GetDocListArgs): Promise<Company[]> => {
    return await dataAPIStore.getDocList<Company>('Company', payload)
  }

  const getDocumentAttachments = async (
    doctype: string,
    docname: string,
  ): Promise<DocAttachment[]> => {
    const filters = [
      ['attached_to_doctype', '=', doctype],
      ['attached_to_name', '=', docname],
    ]
    const fields = ['name', 'file_name', 'file_url', 'creation']

    const docAttachments = await callAPIStore.callInstanceAxiosInstance.get<
      ResponseWrapper<DocAttachment[]>
    >('/api/resource/File', {
      params: {
        fields: JSON.stringify(fields),
        filters: JSON.stringify(filters),
      },
    })

    return docAttachments.data.data
  }

  const getDocumentAssignments = async (
    doctype: string,
    docname: string,
  ): Promise<DocAssigment[]> => {
    const filters = [
      ['reference_type', '=', doctype],
      ['reference_name', '=', docname],
      ['status', '!=', 'Closed'],
      ['status', '!=', 'Cancelled'],
    ]

    const fields = ['allocated_to', 'allocated_to.full_name']

    const docAssignments = await callAPIStore.callInstanceAxiosInstance.get<
      ResponseWrapper<DocAssigment[]>
    >('/api/resource/ToDo', {
      params: {
        fields: JSON.stringify(fields),
        filters: JSON.stringify(filters),
      },
    })

    return docAssignments.data.data
  }

  const removeDocAssignment = async (params: {
    assign_to: string
    doctype: string
    name: string
  }): Promise<void> => {
    await callAPIStore.post('frappe.desk.form.assign_to.remove', {
      ...params,
    })
  }

  const closeDocAssignment = async (params: {
    assign_to: string
    doctype: string
    name: string
  }): Promise<void> => {
    await callAPIStore.post('frappe.desk.form.assign_to.close', {
      ...params,
    })
  }

  const getDoctypePermissions = async (
    doctypes: string[],
  ): Promise<Record<string, Permissions>> => {
    const response = await callAPIStore.post<ResponseWrapper<Record<string, Permissions>>>(
      `${controllerNamePrefix}.doc.get_doctype_permissions`,
      { doctypes },
    )
    return response.data
  }

  const hasDocumentPermission = async (
    params: HasDocumentPermissionParams,
  ): Promise<HasDocumentPermissionResponse> => {
    const response = await callAPIStore.post<ResponseWrapper<HasDocumentPermissionResponse>>(
      `${controllerNamePrefix}.doc.get_document_permission`,
      params,
    )

    return response.data
  }

  /**
   * @returns the new custom image path for the employee image.
   * **/
  const showEmployeeImage = (path: string): string => {
    return `/api/method/${controllerNamePrefix}.files.employee_file?file_url=${encodeURIComponent(path)}`
  }

  /**
   * Get unread todos for logged in user.
   * **/
  const getUnreadTodos = async (limit = 10): Promise<false | GetUnreadTodos> => {
    try {
      const response = await callAPIStore.post<ResponseWrapper<GetUnreadTodos>>(
        `${controllerNamePrefix}.doc.get_unread_todos`,
        { limit },
      )
      return response.data
    } catch (responseError) {
      console.error('getUnreadTodos Error:', responseError)
      return false
    }
  }

  // ── Time Tracking Timer API ──────────────────────────────────────────

  const timerGetCurrentEmployee = async (): Promise<TimerEmployee> => {
    const response = await callAPIStore.post<ResponseWrapper<TimerEmployee>>(
      `${controllerNamePrefix}.timesheet_timer.get_current_employee`,
      {},
    )
    return response.data
  }

  const timerGetStatus = async (): Promise<null | TimerStatusResponse> => {
    const response = await callAPIStore.post<ResponseWrapper<null | TimerStatusResponse>>(
      `${controllerNamePrefix}.timesheet_timer.get_timer_status`,
      {},
    )
    return response.data ?? null
  }

  const timerStart = async (params: {
    activity_type: string
    customer?: null | string | undefined
    description?: string
    is_billable?: boolean
    project?: null | string | undefined
    title?: string
  }): Promise<TimerNameResult> => {
    const response = await callAPIStore.post<ResponseWrapper<TimerNameResult>>(
      `${controllerNamePrefix}.timesheet_timer.start_timer`,
      {
        activity_type: params.activity_type,
        customer: params.customer ?? undefined,
        description: params.description ?? '',
        is_billable: params.is_billable ?? true,
        project: params.project ?? undefined,
        title: params.title ?? '',
      },
    )
    return response.data
  }

  const timerStop = async (params: {
    timesheet_name: string
    total_elapsed_ms: number
  }): Promise<TimerActionResult> => {
    const response = await callAPIStore.post<ResponseWrapper<TimerActionResult>>(
      `${controllerNamePrefix}.timesheet_timer.stop_timer`,
      params,
    )
    return response.data
  }

  const timerUpdateMetadata = async (params: {
    activity_type?: null | string
    customer?: null | string
    description?: null | string
    is_billable?: boolean | null
    project?: null | string
    timesheet_name: string
    title?: null | string
  }): Promise<TimerActionResult> => {
    const response = await callAPIStore.post<ResponseWrapper<TimerActionResult>>(
      `${controllerNamePrefix}.timesheet_timer.update_timer_metadata`,
      params,
    )
    return response.data
  }

  const timerDiscard = async (timesheetName: string): Promise<TimerActionResult> => {
    const response = await callAPIStore.post<ResponseWrapper<TimerActionResult>>(
      `${controllerNamePrefix}.timesheet_timer.discard_timer`,
      { timesheet_name: timesheetName },
    )
    return response.data
  }

  const timerCreateManualEntry = async (params: {
    activity_type: string
    customer?: null | string | undefined
    date?: null | string | undefined
    description?: string
    is_billable?: boolean
    project?: null | string | undefined
    time_blocks: { end_time: string; start_time: string }[]
    title?: string
  }): Promise<{ name: string }> => {
    const response = await callAPIStore.post<ResponseWrapper<{ name: string }>>(
      `${controllerNamePrefix}.timesheet_timer.create_manual_entry`,
      {
        activity_type: params.activity_type,
        customer: params.customer ?? undefined,
        date: params.date ?? undefined,
        description: params.description ?? '',
        is_billable: params.is_billable ?? true,
        project: params.project ?? undefined,
        // Frappe does not auto-deserialize nested arrays – must stringify manually
        time_blocks: JSON.stringify(params.time_blocks),
        title: params.title ?? '',
      },
    )
    return response.data
  }

  const timerGetOptions = async (customer?: null | string): Promise<TimerOptionsResponse> => {
    const response = await callAPIStore.post<ResponseWrapper<TimerOptionsResponse>>(
      `${controllerNamePrefix}.timesheet_timer.get_time_tracking_options`,
      { customer: customer ?? undefined },
    )
    return response.data
  }

  return {
    acceptSubscription,
    addTagsToDocuments,
    applyManagementSuggestions,
    cancelSignatureRequest,
    checkCanCreateAutoRepeat,
    checkCanCreateCreditNoteFromPurchaseInvoice,
    checkCanCreateCreditNoteFromSalesInvoice,
    checkCanCreateDeliveryNote,
    checkCanCreateDunning,
    checkCanCreatePayment,
    checkCanCreatePaymentFromPurchaseInvoice,
    checkCanCreateProject,
    checkCanCreateSalesInvoiceFromSalesOrder,
    checkDatevAttachFilesConfigured,
    closeDocAssignment,
    completeCurrentUserOnboarding,
    createActivityDocumentation,
    createAddress,
    createAndSendSignature,
    createBankAccount,
    createEmail,
    createEvent,
    createNewPrintFormat,
    createOrganization,
    createPerson,
    createPrintDesignerFormat,
    deleteDocuments,
    duplicatePrintDesignerFormat,
    fetchKPIData,
    findManagementPersonMatches,
    generateAIEmailText,
    getActivityDocumentationList,
    getActivityEntry,
    getAddress,
    getAllContacts,
    getAllTags,
    getBankAccountList,
    getBankList,
    getCalendarEvents,
    getCompanyList,
    getCompanyManagementAI,
    getCompletionRate,
    getCountries,
    getCurrentUserOnboardingStatus,
    getCustomerGroupList,
    getDistributionListsForContact,
    getDistributionListsForOrganization,
    getDocListAddress,
    getDocListDynamicLink,
    getDocListEmployee,
    getDocListUser,
    getDoctypeActivityFeed,
    getDoctypeItems,
    getDoctypePermissions,
    getDoctypeRelations,
    getDoctypeTargetRelations,
    getDocumentActivityContext,
    getDocumentAssignments,
    getDocumentAttachments,
    getEmailAccountsForUser: getUserEmailAccounts,
    getIndustryList,
    getKanbanBoardsForRefDoctype,
    getKanbanStatesByDoctype,
    getOrganization: getOrganizationDoc,
    getOrganizationAIInfo,
    getOrganizationList,
    getPaymentTermsTemplates,
    getPerson,
    getPersonDoc,
    getPersonList,
    getPhoneTypes,
    getProjectBillingSummary,
    getReportsForRefDoctype,
    getRoles,
    getSearchResults,
    getShareUsers,
    getSignatureRequest,
    getSignatureRequests,
    getSubscribedNewsletters,
    getSuggestedSigners,
    getSupplierGroupList,
    getTagsforTypes,
    getTWInvestorType,
    getTwoFactorAuthSetup,
    getTWOtherType,
    getUnreadCommunications,
    getUnreadDashboard,
    getUnreadTodos,
    getUserCalendars,
    getWebLinkTypes,
    hasDocumentPermission,
    markAsRead,
    removeDocAssignment,
    removeTagsFromDocument,
    resendSignatureRequest,
    resendUserInvitation,
    resetUserPassword,
    setKanbanBoardPrivateState,
    setPermissionForAllUsersForOneDocument,
    showEmployeeImage,
    submitSignature,
    timerCreateManualEntry,
    timerDiscard,
    timerGetCurrentEmployee,
    timerGetOptions,
    timerGetStatus,
    timerStart,
    timerStop,
    timerUpdateMetadata,
    toggleFavoriteState,
    toggleLiked,
    unsubscribeFromNewsletter: unsubscribeFromNewsletters,
    updateActivityItem,
    updateAddress,
    updateBankAccount,
    updateEmailItem,
    updateEventItem,
    updateOrganizationList,
    updatePerson,
    updatePersonList,
    updateRatingsByDoctype,
    updateUserPassword,
  }
})

export {
  type EmployeeListParameterList,
  type HasDocumentPermissionResponse,
  type TranslatedTWPhoneType,
  type TranslatedTWRole,
  type TranslatedTWWeblinkType,
  useFrappeNestCustomAPIStore,
  type UserOnboardingStatusResponse,
}
